import { useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Hand, Play, Send } from 'lucide-react'
import { Button, FormError, LoadFailure, Skeleton, useLoadingWindow } from '../../core/index.js'
import { useCensus, useHoldScheduleRun, useMonthHealth, useSchedulePeriod } from '../hooks.js'
import type { HealthFinding, MonthHealthView, SchedulePeriodView, ShiftTypeKey, UnitHealth } from '../types.js'
import { filledPctOf } from '../types.js'
import { HealthFixLink } from './MonthHealthCard.js'
import { isPastMonth, monthRange, requiredBasisText, SEVERITY_DOT, sortFindings } from './monthHealthData.js'
import { MonthHeatmap } from './heatmap/MonthHeatmap.js'
import { MonthTrends } from './MonthTrends.js'
import { monthBounds } from './monthTrendsData.js'
import { todayDate } from './scheduleCalendarData.js'

const SHIFT_TYPES: readonly ShiftTypeKey[] = ['day', 'evening', 'night']
const SHIFT_LABEL: Record<ShiftTypeKey, string> = { day: 'Day', evening: 'Evening', night: 'Night' }

/** "December". */
const monthName = (period: string): string => new Date(`${period}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })
/** "Nov 3". */
const shortDate = (iso: string, timezone: string): string => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone })
/** "Sat Nov 7 · 6:00 AM". */
const whenOf = (iso: string, timezone: string): string => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: timezone })

function stepMonth(period: string, by: number): string {
  const [year, month] = period.split('-').map(Number)
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1 + by, 1)).toISOString().slice(0, 7)
}

const STATUS_CHIP: Record<SchedulePeriodView['status'], { label: string; className: string }> = {
  bidding: { label: 'Bidding', className: 'bg-marine-lt text-marine-dk' },
  closed: { label: 'Bidding closed · not built', className: 'bg-gold-lt text-ink' },
  built: { label: 'Built · in review', className: 'bg-gold-lt text-ink' },
  released: { label: 'Released', className: 'bg-green-lt text-green' },
}

/** A month that has ended (demo history plan H0): released and worked, or never scheduled here. Nothing in it is still to decide. */
const PAST_CHIP = {
  worked: { label: 'Worked', className: 'bg-silver-lt text-ink' },
  unscheduled: { label: 'Not scheduled here', className: 'bg-silver-lt text-ink-mid' },
} as const

type StepState = 'done' | 'now' | 'next'

/** The month's road as the mockup draws it: published, bidding, built, review, release, change window, open board. */
function timelineOf(view: SchedulePeriodView, health: MonthHealthView | null, past: boolean): { title: string; detail: string; state: StepState }[] {
  const tz = view.timezone
  const now = Date.now()
  const run = view.latestRun
  const released = view.status === 'released'
  const closed = Date.parse(view.preferencesCloseAt) <= now
  const releaseAt = view.engineReleasesAt ?? null
  const changeClose = Date.parse(view.changeWindowClosesAt)
  const requiredBasis = health ? requiredBasisText(health.byUnit) : null
  const steps: { title: string; detail: string; done: boolean }[] = [
    { title: 'Shifts published', detail: `${view.shiftsPublished} seats${health?.fill?.basis === 'plan' ? ` · ${health.fill.required} required at the census${requiredBasis ? ` (${requiredBasis})` : ''}` : ''}`, done: view.shiftsPublished > 0 },
    // A month released before its close has finished bidding: what is saved later no longer changes it.
    { title: closed ? 'Bidding closed' : released ? 'Bidding ended at release' : 'Bidding open', detail: `${view.preferencesOpenAt ? `${shortDate(view.preferencesOpenAt, tz)} → ` : 'closes '}${shortDate(view.preferencesCloseAt, tz)} · ${view.preferencesSaved} of ${view.bidding} bid`, done: closed || released },
    { title: run ? (run.builtAtClose ? 'Built at the close' : 'Built') : 'Build', detail: run ? `${shortDate(run.ranAt, tz)}${health?.fill ? ` · ${Math.round(100 - health.fill.openPct)}% filled` : ''}` : view.buildAtClose ? 'when bidding closes' : 'by staff', done: run !== null },
    // A release date already past with nothing built is not a date to promise: the engine releases a month only once it is built.
    { title: 'Review', detail: !run && releaseAt && Date.parse(releaseAt) <= now ? 'after the build · hold if not ready' : releaseAt ? `until ${shortDate(releaseAt, tz)} · hold if not ready` : 'staff release when ready', done: released },
    { title: 'Release', detail: released ? 'every worker told their month' : !run && releaseAt && Date.parse(releaseAt) <= now ? 'when the review ends · automatic unless held' : releaseAt ? `${whenOf(releaseAt, tz)} · automatic unless held` : 'by staff', done: released },
    { title: 'Change window', detail: `swaps and changes, settled by score · to ${shortDate(view.changeWindowClosesAt, tz)}`, done: released && changeClose <= now },
    { title: 'Open board', detail: past ? 'the month has been worked' : 'open seats offered to eligible workers · locks before each shift', done: past && released },
  ]
  // A month that has ended has no step happening now: what happened is done, what did not stays undone.
  const firstOpen = past ? -1 : steps.findIndex((step) => !step.done)
  return steps.map((step, index) => ({ title: step.title, detail: step.detail, state: step.done ? 'done' : index === firstOpen ? 'now' : 'next' }))
}

function Tile({ label, children, note, tone = 'plain' }: { label: string; children: ReactNode; note: ReactNode; tone?: 'plain' | 'block' }) {
  return (
    <div className={`flex min-w-0 flex-1 basis-52 flex-col gap-1 rounded border px-4 py-3 ${tone === 'block' ? 'border-coral bg-white' : 'border-marine/15 bg-white'}`}>
      <span className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</span>
      {children}
      <span className="text-[11px] leading-snug text-ink-mid">{note}</span>
    </div>
  )
}

function FillTable({ units, built, links }: { units: readonly UnitHealth[]; built: boolean; links: ReactNode }) {
  const cell = (required: number, held: number): ReactNode => {
    if (required === 0) {
      return <span className="text-ink-mid">—</span>
    }
    if (!built) {
      return <span className="text-ink-mid">{required} required</span>
    }
    const pct = Math.round((held / required) * 100)
    return (
      <span>
        <span className={`font-bold ${pct >= 80 ? 'text-green' : 'text-coral'}`}>{pct}%</span> · {held} of {required} · {required - held} open
      </span>
    )
  }
  const totals = (shiftType: ShiftTypeKey) => units.reduce((sum, unit) => { const entry = unit.byShift?.find((row) => row.shiftType === shiftType); return { required: sum.required + (entry?.required ?? 0), held: sum.held + (entry?.held ?? 0) } }, { required: 0, held: 0 })
  return (
    <section className="flex flex-col gap-3 rounded border border-marine/15 bg-white px-4 py-3.5">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-[11px] font-bold uppercase tracking-wide text-marine">Fill by unit and shift</h2>
        <span className="text-[11px] text-ink-mid">required by the plan · filled from the roster · open for PRN, agency and the board after release</span>
        <span className="flex-1" />
        {links}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[40rem] text-xs">
          <thead>
            <tr className="bg-marine-dk text-left text-[10px] font-bold uppercase tracking-wide text-white/70">
              <th className="px-2 py-1.5">Unit</th>
              {SHIFT_TYPES.map((shiftType) => (
                <th key={shiftType} className="px-2 py-1.5">
                  {SHIFT_LABEL[shiftType]}
                </th>
              ))}
              <th className="px-2 py-1.5">Month</th>
            </tr>
          </thead>
          <tbody>
            {units.map((unit) => (
              <tr key={unit.unitId} className="border-b border-marine/10">
                <td className="px-2 py-2 font-bold text-ink">{unit.name}</td>
                {SHIFT_TYPES.map((shiftType) => {
                  const entry = unit.byShift?.find((row) => row.shiftType === shiftType)
                  return (
                    <td key={shiftType} className="px-2 py-2">
                      {cell(entry?.required ?? 0, entry?.held ?? 0)}
                    </td>
                  )
                })}
                <td className="px-2 py-2">{cell(unit.seatsRequired, unit.seatsHeld)}</td>
              </tr>
            ))}
            <tr>
              <td className="px-2 py-2 font-black text-ink">Facility</td>
              {SHIFT_TYPES.map((shiftType) => (
                <td key={shiftType} className="px-2 py-2">
                  {cell(totals(shiftType).required, totals(shiftType).held)}
                </td>
              ))}
              <td className="px-2 py-2">{cell(units.reduce((sum, unit) => sum + unit.seatsRequired, 0), units.reduce((sum, unit) => sum + unit.seatsHeld, 0))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  )
}

/** "Released Sep 27 by the engine · worked", or why a past month has no schedule to read back. */
function pastLine(view: SchedulePeriodView, run: SchedulePeriodView['latestRun'], tz: string): string {
  if (view.status !== 'released') {
    return `No schedule was built and released for ${monthName(view.period)} here`
  }
  const released = view.releasedRun ?? run
  return `Released${released?.releasedAt ? ` ${shortDate(released.releasedAt, tz)}` : ''}${released?.releasedAutomatically ? ' by the engine' : ''} · worked`
}

/**
 * In place of what needs a person and the release box, for a month that has ended: what became of it, and the way
 * into the run and the board's record of the month, both read-only by now.
 */
function PastMonthNote({ period, released, releasedLine, onOpen }: { period: string; released: boolean; releasedLine: string | null; onOpen: (section: MonthDetail) => void }) {
  return (
    <section className="flex flex-col gap-2 rounded border border-marine/15 bg-white px-4 py-3.5">
      <h2 className="text-[11px] font-bold uppercase tracking-wide text-marine">{monthName(period)} is over</h2>
      {released ? (
        <>
          <p className="text-xs leading-relaxed text-ink">
            {releasedLine}. Every day of it has been worked: who held which shift is in the run, and the swaps, pickups and census changes after the release are in the board&rsquo;s record. Each worker&rsquo;s score for the month is on the Roster.
          </p>
          <div className="flex flex-wrap gap-4">
            <button type="button" className={LINK_CLASS} onClick={() => onOpen('build')}>
              The run, worker by worker
            </button>
            <button type="button" className={LINK_CLASS} onClick={() => onOpen('board')}>
              After the release ›
            </button>
          </div>
        </>
      ) : (
        <p className="text-xs leading-relaxed text-ink">
          Nothing was built and released for {monthName(period)} at this facility, so there is no schedule to read back. Shift outcomes reported for the month still count in each worker&rsquo;s score.
        </p>
      )}
    </section>
  )
}

interface MonthOverviewProps {
  /** Whether the overview is on screen: it opens the loading window only then. */
  shown: boolean
  facilityId: string
  dashboardHref: string
  configHref: string
  onAddWorker?: () => void
  /** Opens one of the month's detail views in place of the overview. */
  onOpen: (section: MonthDetail) => void
}

/** The detail views the overview opens: the findings in full, the run, the board after the release, census planning and the published shifts. */
export type MonthDetail = 'health' | 'build' | 'board' | 'census' | 'publish'

const LINK_CLASS = 'text-xs font-bold text-marine hover:text-marine-dk'

/** The overview's ways into the detail, as the mockup puts them beside the fill by unit and shift. */
function DetailLinks({ contests, onOpen }: { contests: number | null; onOpen: (section: MonthDetail) => void }) {
  return (
    <>
      <button type="button" className={LINK_CLASS} onClick={() => onOpen('build')}>
        The run, worker by worker
      </button>
      {contests !== null && contests > 0 && (
        <button type="button" className={LINK_CLASS} onClick={() => onOpen('build')}>
          Contests ({contests})
        </button>
      )}
      <button type="button" className={LINK_CLASS} onClick={() => onOpen('census')}>
        Census planning
      </button>
      <button type="button" className={LINK_CLASS} onClick={() => onOpen('publish')}>
        Published shifts
      </button>
    </>
  )
}

/**
 * The Month tab's overview, as the mockup draws it (facility operations dashboard plan §3): the month with arrows and
 * its status, its road from publishing to the open board, four tiles (open shifts against the facility's limit, what
 * blocks the release, warnings, who got what), what needs a person before the release beside the release box, and the
 * fill by unit and shift.
 */
export function MonthOverview({ shown: onScreen, facilityId, dashboardHref, configHref, onAddWorker, onOpen }: MonthOverviewProps) {
  // The month the tab opens on is the current one (26 October 2026, the user's rule); the arrows step from there.
  const [period, setPeriod] = useState<string>(() => todayDate().slice(0, 7))
  const health = useMonthHealth(facilityId, period)
  const shown = health.data?.period ?? period
  const periodQuery = useSchedulePeriod(facilityId, shown)
  const view = periodQuery.data ?? null
  const hold = useHoldScheduleRun(facilityId)
  const range = monthRange(new Date(), view?.firstPeriod ?? null)
  // The month's census by day, for the Trends panel.
  const census = useCensus(facilityId, shown ? monthBounds(shown) : null)
  useLoadingWindow({
    shown: onScreen,
    visit: `month:${facilityId}`,
    title: shown ? `Opening ${monthName(shown)}` : 'Opening the month',
    rank: 1,
    sources: [
      { key: 'month-health', label: 'The month’s health', query: health },
      { key: 'month-period', label: 'Where the month stands', query: periodQuery },
      { key: 'month-census', label: 'The month’s census', query: census },
    ],
  })
  if (health.isError) {
    return <LoadFailure message="Could not load the month." onRetry={() => void health.refetch()} retrying={health.isFetching} />
  }
  const data = health.data
  if (!data || !view || !shown) {
    return <Skeleton lines={6} label="Loading the month" />
  }
  const tz = view.timezone
  const blocks = sortFindings(data.findings.filter((finding) => finding.severity === 'block'))
  const warnings = sortFindings(data.findings.filter((finding) => finding.severity === 'warn'))
  const information = sortFindings(data.findings.filter((finding) => finding.severity === 'info'))
  const fill = data.fill ?? null
  const minFilledPct = fill?.minFilledPct ?? null
  const over = fill !== null && minFilledPct !== null && filledPctOf(fill) < minFilledPct
  const run = view.latestRun
  const held = run?.held ?? null
  const releaseAt = view.engineReleasesAt ?? null
  const past = isPastMonth(shown, new Date())
  const chip = past ? PAST_CHIP[view.status === 'released' ? 'worked' : 'unscheduled'] : STATUS_CHIP[view.status]
  const listed: HealthFinding[] = [...blocks, ...warnings, ...information].slice(0, 8)
  const steps = timelineOf(view, data, past)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous month" disabled={shown <= range.first} className="rounded p-1 text-marine hover:bg-marine/10 disabled:opacity-30" onClick={() => setPeriod(stepMonth(shown, -1))}>
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-black text-ink">{monthName(shown)}</h1>
          <button type="button" aria-label="Next month" disabled={shown >= range.last} className="rounded p-1 text-marine hover:bg-marine/10 disabled:opacity-30" onClick={() => setPeriod(stepMonth(shown, 1))}>
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${chip.className}`}>{chip.label}</span>
        <span className="text-xs text-ink-mid">{past ? pastLine(view, run, tz) : view.status === 'released' ? 'On the board; the Today tab runs its days' : releaseAt && run ? `Releases ${whenOf(releaseAt, tz)} unless you hold it` : run ? 'Staff release this month' : `Bidding ${Date.parse(view.preferencesCloseAt) <= Date.now() ? 'closed' : 'closes'} ${shortDate(view.preferencesCloseAt, tz)}`}</span>
      </div>

      <ol className="flex flex-wrap gap-1 rounded border border-marine/15 bg-white px-2 pb-2 pt-3">
        {steps.map((step) => (
          <li key={step.title} className={`flex min-w-[8rem] flex-1 flex-col gap-1 border-t-4 px-3 py-2 ${step.state === 'done' ? 'border-green' : step.state === 'now' ? 'rounded-b border-coral bg-coral-lt' : 'border-marine/20'}`}>
            <span className="text-[11px] font-black text-ink">
              {step.title}
              {step.state === 'now' ? ' · now' : ''}
            </span>
            <span className="text-[10px] leading-snug text-ink-mid">{step.detail}</span>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap gap-3">
        {/* The rule is a share filled (at least 20%), so the tile leads with the share filled, never the share open beside it. */}
        <Tile label="Filled from the roster" note={fill ? `${fill.filled} filled from the roster · ${fill.open} open for PRN, agency and the board${over ? ' · short of the minimum' : ''}` : 'Read once the month is built'}>
          {fill ? (
            <>
              <span className={`text-2xl font-black leading-tight ${over ? 'text-coral' : 'text-green'}`}>
                {filledPctOf(fill)}% <span className="text-xs font-bold text-ink-mid">of {fill.required} {fill.basis === 'plan' ? 'required' : 'published'}{minFilledPct !== null ? ` · at least ${minFilledPct}% filled` : ''}</span>
              </span>
              <div className="flex h-2 overflow-hidden rounded-full bg-silver-lt">
                <div className="bg-green" style={{ width: `${100 - fill.openPct}%` }} />
                <div className={over ? 'bg-coral' : 'bg-gold'} style={{ width: `${fill.openPct}%` }} />
              </div>
            </>
          ) : (
            <span className="text-2xl font-black leading-tight text-ink-mid">—</span>
          )}
        </Tile>
        {/* What blocks or warns about a release means nothing once the month is over. */}
        {!past && (
          <>
            <Tile label="Blocks the release" tone={blocks.length > 0 ? 'block' : 'plain'} note={blocks[0]?.message ?? 'Nothing holds the release'}>
              <span className={`text-2xl font-black leading-tight ${blocks.length > 0 ? 'text-coral' : 'text-ink'}`}>{blocks.length}</span>
            </Tile>
            <Tile label="Warnings" note={warnings.slice(0, 2).map((finding) => finding.message).join(' · ') || 'None'}>
              <span className="text-2xl font-black leading-tight text-ink">{warnings.length}</span>
            </Tile>
          </>
        )}
        <Tile label="Who got what" note={run ? `${run.totals.scheduled} scheduled from their asks · ${run.totals.assigned} assigned · ${run.totals.nothingFit} nothing fit · ${run.totals.noPreferences} no preferences${run.totals.daysOffTaken ? ` · ${run.totals.daysOffTaken} days off taken for coverage` : ''}` : 'Read once the month is built'}>
          <span className="text-2xl font-black leading-tight text-ink">
            {run ? run.totals.workers : '—'} <span className="text-xs font-bold text-ink-mid">workers</span>
          </span>
        </Tile>
      </div>

      <MonthHeatmap facilityId={facilityId} period={shown} todayHref={`${dashboardHref}?tab=today`} onChoosePeriod={(chosen) => setPeriod(chosen >= range.first && chosen <= range.last ? chosen : shown)} />

      <MonthTrends query={census} period={shown} today={new Date().toLocaleDateString('en-CA', { timeZone: tz })} />

      {past ? (
        <PastMonthNote period={shown} released={view.status === 'released'} releasedLine={run ? pastLine(view, run, tz) : null} onOpen={onOpen} />
      ) : (
      <div className="grid items-start gap-5 lg:grid-cols-[2fr_1fr]">
        <section className="flex min-w-0 flex-col rounded border border-marine/15 bg-white px-4 py-3.5">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-[11px] font-bold uppercase tracking-wide text-marine">What needs you before the release</h2>
            <span className="text-[11px] text-ink-mid">each with the one thing that fixes it</span>
          </div>
          {listed.length === 0 ? (
            <p className="py-3 text-xs text-ink-mid">Nothing. The month is ready.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-marine/10">
              {listed.map((finding, index) => (
                <li key={`${finding.key}-${finding.unitId ?? ''}-${finding.shiftType ?? ''}-${finding.role ?? ''}-${index}`} className="grid grid-cols-[1rem_1fr_auto] items-start gap-3 py-3">
                  <span className={`mt-1.5 h-2 w-2 rounded-full ${SEVERITY_DOT[finding.severity]}`} aria-label={finding.severity} />
                  <span className="text-xs leading-relaxed text-ink">{finding.message}</span>
                  <HealthFixLink action={finding.action} dashboardHref={dashboardHref} configHref={configHref} onAddWorker={onAddWorker} />
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="self-start pt-2 text-xs font-bold text-marine hover:text-marine-dk" onClick={() => onOpen('health')}>
            {data.findings.length > listed.length ? `All ${data.findings.length} findings` : 'The month’s health in full'}
          </button>
        </section>

        <section className={`flex flex-col gap-3 rounded border px-4 py-3.5 ${held ? 'border-coral/40 bg-coral-lt/40' : 'border-gold/60 bg-gold-lt/60'}`}>
          <h2 className="text-[11px] font-bold uppercase tracking-wide text-marine">The release</h2>
          {view.status === 'released' ? (
            <p className="text-xs leading-relaxed text-ink">Released{run?.releasedAt ? ` ${whenOf(run.releasedAt, tz)}` : ''}{run?.releasedAutomatically ? ' by the engine' : ''}. The change window and the open board follow it.</p>
          ) : !run ? (
            <p className="text-xs leading-relaxed text-ink">Nothing to release yet: the month is built {view.buildAtClose ? 'when bidding closes' : 'by staff'}.</p>
          ) : (
            <>
              <p className="text-sm font-black text-ink">{releaseAt ? whenOf(releaseAt, tz) : 'Staff release'}</p>
              <p className="text-xs leading-relaxed text-ink-mid">
                {held ? `Held${held.note ? ` - ${held.note}` : ''}. The engine will not release it.` : releaseAt ? 'The engine releases it then, tells every worker their month, opens the change window and puts the open seats on the board - unless a block still stands.' : 'This facility releases by hand.'}
                {blocks.length > 0 && !held ? ` ${blocks.length === 1 ? 'One block stands' : `${blocks.length} blocks stand`}: clear it, or release past it with a reason.` : ''}
              </p>
              <Button icon={Send} onClick={() => onOpen('build')}>
                Release now
              </Button>
              {releaseAt && (
                <Button icon={held ? Play : Hand} variant="secondary" disabled={hold.isPending} onClick={() => hold.mutate({ runId: run.runId, hold: !held })}>
                  {held ? 'Let the engine release it' : 'Hold the release'}
                </Button>
              )}
              <FormError error={hold.error} fallback="That did not go through. Try again." />
            </>
          )}
          <div className="flex flex-col gap-1 border-t border-marine/15 pt-2">
            <button type="button" className="self-start text-[10px] font-bold uppercase tracking-wide text-marine hover:text-marine-dk" onClick={() => onOpen('board')}>
              After the release ›
            </button>
            <p className="text-[11px] leading-relaxed text-ink-mid">Open seats are offered to eligible workers in the facility's order, then first come; a census change re-evaluates the day within the hour.</p>
          </div>
        </section>
      </div>
      )}

      {data.byUnit.length > 0 ? (
        <FillTable units={data.byUnit} built={data.runId !== null} links={<DetailLinks contests={run?.totals.contests ?? null} onOpen={onOpen} />} />
      ) : (
        <div className="flex flex-wrap gap-4">
          <DetailLinks contests={run?.totals.contests ?? null} onOpen={onOpen} />
        </div>
      )}
    </div>
  )
}
